import React, { Component, useCallback, useEffect, useRef, useState } from "react";
import styles from "./Threshold.module.scss";
import copy from "../../assets/copy/copy.json"
import Button from "../UI/Button/Button";
import { startOfWeek, addDays, format, startOfMonth, endOfMonth } from "date-fns";
import { Requests } from "../../api/IdentityServerRequests/Requests";
import SetThreshold from "../Availability/SetThreshold";
import Icon from "../UI/Icon/Icon";

class Legend extends Component {
  render() {
    return (
      <div className="legend">
        <div className="thresholdUnmet"></div>
        <span>Unmet</span>
        <div className="thresholdMet"></div>
        <span>Met</span>
      </div>
    )
  }
}

export default function MembersThreshold(props) {
  let minicalContainer = useRef()
  let schedulerContainer = useRef()
  const [selectedDate, setSelectedDate] = useState(new Date())
  const [thresholdData, setThresholdData] = useState(null)
  const [thresholdModal, setThresholdModal] = useState(false)
  const [modifiedThresholds, setModifiedThresholds] = useState([])
  const [monthThresholdData, setMonthThresholdData] = useState(null)
  const [monthInView, setMonthInView] = useState(new Date())
  const requests = new Requests()
  let monday = startOfWeek(selectedDate, { weekStartsOn: 1 })
  let scheduler = window.scheduler

  // create minical and colour in dates with met thresholds
  useEffect(() => {
    scheduler.plugins({ minical: true })
    scheduler.init(schedulerContainer.current, monthInView, "day")
    let calendar = scheduler.renderCalendar({
      container: minicalContainer.current,
      navigation: true,
      date: monthInView,
      handler: (date) => setSelectedDate(date)
    })
    scheduler.clearAll();

    if (monthThresholdData) {
      minicalMonthNavigation();
    }
    outlineDates();

    return () => {
      scheduler.destroyCalendar(calendar)
    }
  }, [monthThresholdData, props.width])

  const minicalMonthNavigation = () => {
    // add event listeners to the forward/back arrows for colouring the events
    highlightDates(monthThresholdData)
    let minical = document.querySelector('.dhx_mini_calendar')
    let nextArrow = minical.querySelector('.dhx_cal_next_button')
    let prevArrow = minical.querySelector('.dhx_cal_prev_button')
    nextArrow.addEventListener('click', minicalMonthNavigation)
    prevArrow.addEventListener('click', minicalMonthNavigation)
    nextArrow.addEventListener('click', onMonthChange)
    prevArrow.addEventListener('click', onMonthChange)
  }

  const onMonthChange = () => {
    const firstDayElement = document.querySelector('.dhx_cal_month_cell:not(.dhx_before)')
    const monthStartDate = new Date(firstDayElement.attributes['data-cell-date'].nodeValue)
    setMonthInView(a => monthStartDate)
  }

  const highlightDates = (dates) => {
    // highlight all cells, then selectively remove highlight
    const cells = document.querySelectorAll('.dhx_month_head')
    cells.forEach(cell => {
      const classList = [...cell.parentElement.classList]
      if ((!classList.includes('dhx_before')) && (!classList.includes('dhx_after'))) {
        cell.classList.add('dhx_year_event')
      }
      const cellDate = new Date(cell.parentElement.attributes['data-cell-date'].nodeValue)
      // match with date in threshold date
      const matchingDate = dates.find(date => {
        const thresholdDate = new Date(date.date)
        return (thresholdDate.valueOf() === cellDate.valueOf())
      })
      if (matchingDate?.thresholdReached) {
        cell.classList.remove('dhx_year_event')
      }
    })
  }

  const outlineDates = () => {
    // remove all highlights
    const cells = document.querySelectorAll('.dhx_month_head')
    cells.forEach(cell =>
      cell.classList.remove('dhx_calendar_click')
    )
    const dates = document.querySelectorAll('.dhx_cal_month_cell')
    const currentDate = new Date(format(selectedDate, "yyyy-MM-dd 00:00"))
    const sunday = addDays(monday, 6)
    dates.forEach(date => {
      const dayElement = date.querySelector('.dhx_month_head')
      const dayDate = new Date(date.attributes['data-cell-date'].nodeValue)
      if (props.width > 1300) {
        // outline current week if on desktop
        if (dayDate >= monday && dayDate <= sunday) {
          dayElement.classList.add('dhx_calendar_click')
        }
      } else {
        // outline current day if not on desktop
        if (dayDate >= currentDate && dayDate <= currentDate) {
          dayElement.classList.add('dhx_calendar_click')
        }
      }
    })
  }

  useEffect(() => {
    if (modifiedThresholds.length) {
      const dateFrom = format(startOfMonth(monthInView), "yyyy-MM-dd")
      const dateTo = format(endOfMonth(monthInView), "yyyy-MM-dd")
      const API_ENDPOINT = requests.getThresholdMonth
      requests.callAPI(API_ENDPOINT, { dateFrom, dateTo }, props.pagingNumber).then((data) => {
        if (data?.status === 200) {
          setMonthThresholdData(a => data.data.dateAvailable)
        }
      })
    }
  }, [modifiedThresholds, monthInView])

  // request paging group threshold and member numbers for populating week table
  useEffect(() => {
    if (modifiedThresholds.length) {
      const dateFrom = format(monday, 'yyyy-MM-dd')
      const dateTo = format(addDays(monday, 6), 'yyyy-MM-dd')
      const API_ENDPOINT = requests.getThresholdMonth
      requests.callAPI(API_ENDPOINT, { dateFrom, dateTo }, props.pagingNumber).then((data) => {
        if (data?.status === 200) {
          setThresholdData(data.data.dateAvailable)
        }
      })
      outlineDates();
    }
  }, [modifiedThresholds, selectedDate])

  // load user-specified thresholds for rows in table and set threshold popup modal 
  useEffect(() => {
    const API_ENDPOINT = requests.getPagingGroup
    requests.callAPI(
      API_ENDPOINT,
      props.pagingNumber
    ).then((data) => {
      if (data?.status === 200) {
        const qualificationThresholds = data?.data.settings.qualificationPaging.map(qualPaging => {
          const qualification = qualPaging.qualification
          return (
            {
              code: qualification.id,
              name: qualification.name,
              threshold: qualPaging.thresholdCount,
            }
          )
        }).filter(qualification => qualification.threshold !== null)
        const anyMembers = {
          name: "Any member",
          code: 0,
          threshold: data?.data.settings.thresholdByPeopleCount || 0
        }
        setModifiedThresholds(qualificationThresholds.concat(anyMembers))
      }
    })
  }, [thresholdModal])

  return (
    <>
      {thresholdModal && <SetThreshold
        isVisible={thresholdModal}
        onClose={() => setThresholdModal(false)}
        backButton={() => setThresholdModal(false)}
        saveButton={() => setThresholdModal(false)}
        qualifications={props.qualifications}
        modifiedThresholds={modifiedThresholds}
        pagingNumber={props.pagingNumber}
      />}
      {props.width > 1300 ? (
        // desktop
        <div>
          <div className={styles.header}>
            <h1>{copy.myAvailabilityScreen.threshold.title}</h1>
            <Button
              label={copy.myAvailabilityScreen.threshold.return}
              content={copy.myAvailabilityScreen.threshold.return}
              variant="btn_myAvailabilityButs"
              styles="threshold"
              icon="icon_exit"
              buttonClick={props.exit}
            />
          </div>
          <div className={styles.landscapeContainer}>
            <div className={styles.body}>
              <div>
                <div className={styles.horizontalLayout + " " + styles.center + " " + styles.landscape}>
                  {props.setPagingGroup}
                </div>
                <div className={styles.schedulerContainer} ref={schedulerContainer}></div>
                <div className={styles.calendarContainer + " thresholds"}>
                  <div className={styles.minicalContainer} ref={minicalContainer}></div>
                  <Legend />
                </div>
              </div>

              <div className={styles.tableSection + " " + styles.landscape}>
                <div className={styles.horizontalLayout}>
                  <span className={styles.heading}>
                    {props.pagingName}
                  </span>
                </div>
                <div className={styles.horizontalLayout + " " + styles.landscape}>
                  <span className={styles.heading + " " + styles.verticalPadding}>
                    {format(monday, "EEEE d LLLL")} - {format(addDays(monday, 6), "EEEE d LLLL")}
                  </span>
                  <Button
                    label={copy.myAvailabilityScreen.threshold.set}
                    content={copy.myAvailabilityScreen.threshold.set}
                    variant="btn_myAvailabilityButs"
                    styles="threshold"
                    hidden={!props.permissionToSetThresholds}
                    buttonClick={() => setThresholdModal(true)}
                  />
                </div>

                <table className={styles.table}>
                  <colgroup>
                    <col className={styles.firstColumn} />
                  </colgroup>
                  <thead>
                    <tr className={styles.headerRow + " " + styles.dataRow}>
                      <th className={styles.headerColumn} scope="col">Qualifications</th>
                      {[...Array(7).keys()].map(e => <th key={e}>
                        <div className={styles.headerRow}>
                          {format(addDays(monday, e), "EEE dd")}
                        </div>
                      </th>)}
                    </tr>
                  </thead>
                  <thead className={styles.top}>
                    <tr className={styles.countRow}>
                      <th className={styles.headerColumn + " " + styles.countCell} scope="col">Count</th>
                      {thresholdData?.map((day, i) => {
                        const totalQualThreshold = day.qualificationThresholds?.reduce((total, current) => total + current.threshold, 0) || 0
                        const totalQualCount = day.qualificationThresholds?.reduce((total, current) => total + current.minimumUserCount, 0) || 0
                        const totalAnyThreshold = day.anyMemberThreshold?.threshold || 0
                        const totalAnyCount = day.anyMemberThreshold?.minimumUserCount || 0
                        return (
                          <td className={styles.countCell} key={`dayCount${i}`}>
                            <div>
                              {day.thresholdReached ? <Icon icon="icon_greenTickThreshold" /> : <Icon icon="icon_redCrossThreshold" />}
                              <span>{`${totalQualCount + totalAnyCount}/${totalQualThreshold + totalAnyThreshold}`}</span>
                            </div>
                          </td>
                        )
                      })}
                    </tr>
                  </thead>
                  <thead>
                    {modifiedThresholds.length ? modifiedThresholds
                      .filter(qual => qual.code !== 0)
                      .map((qual, i) => <tr key={`modifiedThresholds${i}`} className={styles.dataRow}>
                        <th className={styles.headerColumn} scope="col">
                          <div className={styles.qualTag}>
                            {qual.name}
                          </div>
                        </th>
                        {thresholdData?.map((day, j) => {
                          const qualification = day.qualificationThresholds?.find((qualificationThreshold) => qualificationThreshold.qualificationId === qual.code)
                          return (
                            qualification && <td key={`thresholdData${qual.code}${j}`}>
                              <div>
                                {qualification.thresholdReached ? <Icon icon="icon_greenTickThreshold" /> : <Icon icon="icon_redCrossThreshold" />}
                                <span>{`${qualification.minimumUserCount}/${qualification.threshold}`}</span>
                              </div>
                            </td>
                          )
                        }
                        )}
                      </tr>) : <tr></tr>}
                  </thead>
                  <thead>
                    <tr className={styles.dataRow}>
                      <th className={styles.headerColumn} scope="col">{copy.myAvailabilityScreen.threshold.any}</th>
                      {thresholdData?.map((day, i) => {
                        const anyMember = day.anyMemberThreshold
                        return (
                          anyMember ? (
                            <td key={`anyMember${i}`}>
                              <div>
                                {anyMember.thresholdReached ? <Icon icon="icon_greenTickThreshold" /> : <Icon icon="icon_redCrossThreshold" />}
                                <span>{`${anyMember.minimumUserCount}/${anyMember.threshold}`}</span>
                              </div>
                            </td>
                          ) : (
                            <td key={`anyMember${i}`}>
                              <div>
                                <Icon icon="icon_greenTickThreshold" />
                                <span>0/0</span>
                              </div>
                            </td>
                          )
                        )
                      }
                      )}
                    </tr>
                  </thead>
                </table>
              </div>
            </div>
          </div>

        </div >
      ) : (
        // mobile
        ((props.height / props.width) > 1) ? (
          // mobile portrait
          <div>
            <div className={styles.body + " " + styles.portrait}>
              <div className={styles.exitButton}>
                <button
                  className="closeAlertControls__btn -close"
                  onClick={props.exit}
                ></button>
              </div>

              <div className={styles.horizontalLayout + " " + styles.center}>
                {props.setPagingGroup}
              </div>
              <div className={styles.schedulerContainer} ref={schedulerContainer}></div>
              <div className={styles.minicalContainer + " " + styles.portrait} ref={minicalContainer}></div>
              <div className={styles.calendarContainer + " thresholds"}>
                <div className={styles.minicalContainer + " " + styles.portrait} ref={minicalContainer}></div>
                <Legend />
              </div>
              <div className={styles.horizontalRule}></div>
              <div className={styles.horizontalLayout}>
                <span className={styles.padding + " " + styles.heading}>
                  {props.pagingName}
                </span>
              </div>
              <div className={styles.horizontalLayout}>
                <span className={styles.padding + " " + styles.verticalPadding}>{format(selectedDate, "EEEE d LLLL")}</span>
                <Button
                  label={copy.myAvailabilityScreen.threshold.set}
                  content={copy.myAvailabilityScreen.threshold.set}
                  variant="btn_myAvailabilityButs"
                  styles="threshold"
                  hidden={!props.permissionToSetThresholds}
                  buttonClick={() => setThresholdModal(true)}
                />
              </div>
              <div className={styles.tableSection + " " + styles.portrait}>
                <table className={styles.table}>
                  <colgroup>
                    <col className={styles.firstColumn} />
                  </colgroup>
                  <thead>
                    <tr className={styles.headerRow}>
                      <th scope="col" className={styles.headerColumn + " " + styles.portrait}>Qualification requirements</th>
                    </tr>
                  </thead>
                  <thead>
                    {modifiedThresholds.length ? (modifiedThresholds
                      .filter(qual => qual.code !== 0)
                      .map((qual, i) => <tr key={`modifiedThresholds${i}`} className={styles.dataRow}>
                        <th scope="col" className={styles.headerColumn}>
                          <div className={styles.horizontalCell}>
                            {thresholdData?.filter(day => format(new Date(day.date), "yyyy-MM-dd") === format(selectedDate, "yyyy-MM-dd"))
                              .map((day, j) => {
                                const qualification = day.qualificationThresholds?.find((qualificationThreshold) => qualificationThreshold.qualificationId === qual.code)
                                return (
                                  qualification?.thresholdReached ? <Icon key={`modifiedThresholds${qual.code}${j}`} icon="icon_greenTickThreshold" /> : <Icon key={`thresholdData${qual.code}${j}`} icon="icon_redCrossThreshold" />
                                )
                              })}
                            <div className={styles.qualTag + " " + styles.padding}>
                              {qual.name}
                            </div>
                          </div>
                        </th>
                        {thresholdData?.filter(day => format(new Date(day.date), "yyyy-MM-dd") === format(selectedDate, "yyyy-MM-dd"))
                          .map((day, j) => {
                            const qualification = day.qualificationThresholds?.find((qualificationThreshold) => qualificationThreshold.qualificationId === qual.code)
                            return (
                              qualification && <td key={`thresholdData${qual.code}${j}`} className={styles.singleColumn}>
                                <span>{`${qualification.minimumUserCount}/${qualification.threshold}`}</span>
                              </td>
                            )
                          })}
                      </tr>)) : (<tr></tr>)}
                  </thead>
                  <thead>
                    <tr className={styles.dataRow}>
                      <th scope="col" className={styles.headerColumn}>
                        <div className={styles.horizontalCell}>
                          {thresholdData?.filter(day => format(new Date(day.date), "yyyy-MM-dd") === format(selectedDate, "yyyy-MM-dd"))
                            .map((day, i) => {
                              const anyMember = day.anyMemberThreshold
                              return (
                                anyMember ? (
                                  anyMember.thresholdReached ? <Icon key={`anyMemberReached${i}`} icon="icon_greenTickThreshold" /> : <Icon key={`anyMemberReached${i}`} icon="icon_redCrossThreshold" />
                                ) : (
                                  <Icon key={`anyMemberReached${i}`} icon="icon_greenTickThreshold" />
                                )
                              )
                            })
                          }
                          <span className={styles.padding}>{copy.myAvailabilityScreen.threshold.any}</span>
                        </div>
                      </th>
                      {thresholdData?.filter(day => format(new Date(day.date), "yyyy-MM-dd") === format(selectedDate, "yyyy-MM-dd"))
                        .map((day, i) => {
                          const anyMember = day.anyMemberThreshold
                          return (
                            anyMember ? (
                              <td key={`anyMemberThreshold${i}`} className={styles.singleColumn}>
                                <span>{`${anyMember.minimumUserCount}/${anyMember.threshold}`}</span>
                              </td>
                            ) : (
                              <td key={`anyMemberThreshold${i}`} className={styles.singleColumn}>
                                <span>0/0</span>
                              </td>
                            )
                          )
                        })
                      }
                    </tr>
                  </thead>
                </table>
              </div>
            </div>
          </div>
        ) : (
          // mobile landscape
          <div className={styles.landscapeContainer}>
            <div className={styles.body}>
              <div>
                <div className={styles.horizontalLayout + " " + styles.center + " " + styles.landscape}>
                  {props.setPagingGroup}
                </div>
                <div className={styles.schedulerContainer} ref={schedulerContainer}></div>
                <div className={styles.calendarContainer + " thresholds"}>
                  <div className={styles.minicalContainer + " " + styles.portrait} ref={minicalContainer}></div>
                  <Legend />
                </div>
              </div>
              <div className={styles.tableSection + " " + styles.landscape + " " + styles.landscapeMobile}>
                <div className={styles.tableGroup}>
                  <div className={styles.horizontalLayout}>
                    <span className={styles.heading}>
                      {props.pagingName}
                    </span>
                  </div>
                  <div className={styles.horizontalLayout + " " + styles.landscape}>
                    <span className={styles.verticalPadding}>{format(selectedDate, "EEEE d LLLL")}</span>
                    <Button
                      label={copy.myAvailabilityScreen.threshold.set}
                      content={copy.myAvailabilityScreen.threshold.set}
                      variant="btn_myAvailabilityButs"
                      styles="threshold rightSide"
                      hidden={!props.permissionToSetThresholds}
                      buttonClick={() => setThresholdModal(true)}
                    />
                  </div>
                  <span className={styles.horizontalLayout + " " + styles.center + " " + styles.centerFlex}>Qualification requirements</span>
                  <table className={styles.table}>
                    <colgroup>
                      <col className={styles.firstColumn + " " + (props.width < 760) && styles.smallLandscape} />
                    </colgroup>
                    <thead>
                      {modifiedThresholds.length ? (modifiedThresholds
                        .filter(qual => qual.code !== 0)
                        .map((qual, i) => <tr key={`modifiedThresholds${i}`} className={styles.dataRow}>
                          <th scope="col" className={styles.headerColumn}>
                            <div className={styles.horizontalCell}>
                              {thresholdData?.filter(day => format(new Date(day.date), "yyyy-MM-dd") === format(selectedDate, "yyyy-MM-dd"))
                                .map((day, j) => {
                                  const qualification = day.qualificationThresholds?.find((qualificationThreshold) => qualificationThreshold.qualificationId === qual.code)
                                  return (
                                    qualification?.thresholdReached ? <Icon key={`modifiedThresholds${qual.code}${j}`} icon="icon_greenTickThreshold" /> : <Icon key={`modifiedThresholds${qual.code}${j}`} icon="icon_redCrossThreshold" />
                                  )
                                })}
                              <div className={styles.qualTag + " " + styles.padding}>
                                {qual.name}
                              </div>
                            </div>
                          </th>
                          {thresholdData?.filter(day => format(new Date(day.date), "yyyy-MM-dd") === format(selectedDate, "yyyy-MM-dd"))
                            .map((day, i) => {
                              const qualification = day.qualificationThresholds?.find((qualificationThreshold) => qualificationThreshold.qualificationId === qual.code)
                              return (
                                qualification && <td key={`thresholdData${qual.code}${i}`} className={styles.singleColumn}>
                                  <span>{`${qualification.minimumUserCount}/${qualification.threshold}`}</span>
                                </td>
                              )
                            })}
                        </tr>)) : <tr></tr>}
                    </thead>
                    <thead>
                      <tr className={styles.dataRow}>
                        <th scope="col" className={styles.headerColumn}>
                          <div className={styles.horizontalCell}>
                            {thresholdData?.filter(day => format(new Date(day.date), "yyyy-MM-dd") === format(selectedDate, "yyyy-MM-dd"))
                              .map((day, i) => {
                                const anyMember = day.anyMemberThreshold
                                return (
                                  anyMember ? (
                                    anyMember.thresholdReached ? <Icon key={`anyMemberReached${i}`} icon="icon_greenTickThreshold" /> : <Icon key={`anyMemberReached${i}`} icon="icon_redCrossThreshold" />
                                  ) : (
                                    <Icon key={`anyMemberReached${i}`} icon="icon_greenTickThreshold" />
                                  )
                                )
                              })
                            }
                            <span className={styles.padding}>{copy.myAvailabilityScreen.threshold.any}</span>
                          </div>
                        </th>
                        {thresholdData?.filter(day => format(new Date(day.date), "yyyy-MM-dd") === format(selectedDate, "yyyy-MM-dd"))
                          .map((day, i) => {
                            const anyMember = day.anyMemberThreshold
                            return (
                              anyMember ? (
                                <td key={`anyMemberThreshold${i}`} className={styles.singleColumn}>
                                  <span>{`${anyMember.minimumUserCount}/${anyMember.threshold}`}</span>
                                </td>
                              ) : (
                                <td key={`anyMemberThreshold${i}`} className={styles.singleColumn}>
                                  <span>0/0</span>
                                </td>
                              )
                            )
                          })
                        }
                      </tr>
                    </thead>
                  </table>
                </div>
                <div className={styles.exitButton + " " + styles.landscape}>
                  <button
                    className="closeAlertControls__btn -close"
                    onClick={props.exit}
                  ></button>
                </div>
              </div>
            </div>
          </div>
        )
      )}
    </>
  )
}